/*
Author: Jude Fernando
Date: 25/09/2024
Copyright © 2024 Infix!. All rights reserved.
*/

import InputField from "@/components/Atoms/InputField";
import InputLabel from "@/components/Atoms/InputLabel";
import { useResponseId, useUrlType } from "@/components/hooks";
import { RootState } from "@/reducers/store";
import { useValidate } from "@/state/survey/formSlice";
import { BasicAnswerSave, useUpdateAnswersSubmit } from "@/state/survey/saveAnswerSlice";
import { Utility, Validate } from "@/utils";
import { ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { INode } from "../SurveyModel/types/nodeTypes";
import { DECIMAL_3_DIGITS_1_DP_REGEX, TWO_DIGIT_REGEX } from "@/constants/constants";

interface IHeightInput {
  node: INode;
  children?: ReactNode;
  name: string;
  disabled: boolean;
  repeatedElement?: boolean;
  onBlur?: () => void;
  type?: "Number";
}

const HeightInput: React.FC<IHeightInput> = (props: IHeightInput) => {
  const { node, name, disabled, repeatedElement, onBlur } = props;
  const form = useSelector((state: RootState) => state.form);
  const validateInputField = useValidate();
  const updateAnswersDispatch = useUpdateAnswersSubmit();
  const { answerResponseId } = useResponseId();
  const { state = {} } = form[node.name] || {};
  const { isPatient } = useUrlType();

  const getInitialHeightValue = (): number | undefined => {
    const formValue = form[node.name]?.value;
    return typeof formValue === 'number' ? formValue : undefined;
  };

  const [heightCM, setHeight] = useState<number | undefined>(getInitialHeightValue());
  const [unit, setUnit] = useState<'metric' | 'imperial'>(node.unit || 'metric');

  const convertToImperial = (cm?: number): { feet?: number; inches?: number } => {
    if (cm === undefined || isNaN(cm)) {
      return { feet: undefined, inches: undefined };
    }
    let totalInches = cm / 2.54;
    let feet = Math.floor(totalInches / 12);
    let inches = Math.round(totalInches % 12);
    if (inches === 12) {
      feet += 1;
      inches = 0;
    }
    return { feet, inches };
  };

  const { feet, inches } = convertToImperial(heightCM);

  useEffect(() => {
    const formValue = form[node.name]?.value;
    if (typeof formValue === 'number') {
      setHeight(formValue);
    } else {
      setHeight(undefined);
    }
  }, [form, node.name]);

  const handleUnitSwitch = () => {
    if (unit === 'metric') {
      setUnit('imperial');
    } else {
      if (heightCM !== undefined) {
        setHeight(Number(heightCM.toFixed(1)));
      }
      setUnit('metric');
    }
    validateInputField(name, heightCM, true);
  };

  const handleOnBlur = () => {
    if (!isPatient && answerResponseId && !repeatedElement && heightCM !== undefined) {
      const answers: BasicAnswerSave[] = [{
        name: node.name,
        value: Number(heightCM.toFixed(1)),
      }];
      updateAnswersDispatch(answers, answerResponseId);
    }
    if (repeatedElement && onBlur) onBlur();
  };

  const handleHeightCmChange = (value: string) => {
    if (!DECIMAL_3_DIGITS_1_DP_REGEX.test(value) && value !== '') return;

    let newHeight: number | undefined;
    if (value === '') {
      newHeight = 0;
    } else {
      newHeight = Number(value);
    }
    setHeight(newHeight);
    validateInputField(name, newHeight, true);
  };

  const handleHeightFtChange = (value: string) => {
    if (!TWO_DIGIT_REGEX.test(value) && value !== '') return;
    
    const ft = Math.floor(Number(value || 0));
    const currentInches = inches ?? 0;
    const totalInches = (ft * 12) + currentInches;
    const cm = totalInches * 2.54;
    const newHeight = Number(cm.toFixed(1));
    setHeight(newHeight);
    validateInputField(name, newHeight, true);
  };

  const handleHeightInChange = (value: string) => {
    const inchesInput = Math.floor(Number(value || 0));
    if (inchesInput > 11) return;
    const currentFeet = feet ?? 0;
    const totalInches = (currentFeet * 12) + inchesInput;
    const cm = totalInches * 2.54;
    const newHeight = Number(cm.toFixed(1));
    setHeight(newHeight);
    validateInputField(name, newHeight, true);
  };

  const validateCM = (value: string) => {
    if (!DECIMAL_3_DIGITS_1_DP_REGEX.test(value) && value !== '') return false;
    return true;
  };

  const validateFeet = (value: string) => {
    if (!TWO_DIGIT_REGEX.test(value) && value !== '') return false;
    return true;
  };

  const validateInches = (value: string) => {
    if (Number(value) >= 12) return false;
    return true;
  };
    return (
        <div className={`height-weight-input-wrap
        ${Utility.getClassForWidthPx(node)} 
        ${node.name}
        ${Utility.getClassForWidth(node)} 
        ${!Utility.getClassForWidthPx(node) && !Utility.getClassForWidth(node) ? 'flex-1' : ''} ${!repeatedElement ? `user-input ${Utility.getClassForWidth(node)} 
        question-bottom-space-1
         input-border-top` : ''}`}>
            <div className={`${Utility.getClassForWidth(node)}`}>
                <InputLabel label={node.title} labelClasses="input-label"></InputLabel> 
                {node.subTitle ? <InputLabel type="subTitle" label={node.subTitle} labelClasses="input-label"></InputLabel> : ''}
                
                {unit === 'metric' ? (
                    <div>
                    <div className="metric-input-wrap">
                        <InputLabel type="subTitle" label="Centimetres" labelClasses="input-label"></InputLabel>
                        <InputField 
                            disabled={disabled} 
                            form={form} 
                            isInValid={Validate.isInvalid(state)} 
                            propClasses={'height-weight-input-text flex'} 
                            value={heightCM?.toString() || ''} 
                            name={`${name}_cm`}
                            type="Number"
                            onChange={handleHeightCmChange} 
                            onBlur={handleOnBlur} 
                            decimalPlaces={1}
                            validate={validateCM}
                        />
                        
                    </div>
                    <button 
                        type="button" 
                        className="unit-switch-link" 
                        onClick={handleUnitSwitch}
                    >
                        Switch to {unit === 'metric' ? 'imperial' : 'metric'}
                    </button>
                    </div>

                ) : (
                    <div>
                    <div className="imperial-wrap" >
                        <div className="imperial-input-wrap">
                            <InputLabel type="subTitle" label="Feet" labelClasses="input-label"></InputLabel>
                            <InputField 
                                disabled={disabled} 
                                form={form} 
                                isInValid={Validate.isInvalid(state)} 
                                propClasses={'height-weight-input-text flex'} 
                                value={feet?.toString() || ''} 
                                name={`${name}_ft`}
                                onChange={handleHeightFtChange} 
                                onBlur={handleOnBlur} 
                                type="Number"
                                validate={validateFeet}
                            />
                            
                        </div>
                        <div className="imperial-input-wrap">
                            <InputLabel type="subTitle" label="Inches" labelClasses="input-label"></InputLabel>
                            <InputField 
                                disabled={disabled} 
                                form={form} 
                                isInValid={Validate.isInvalid(state)} 
                                propClasses={'height-weight-input-text flex'}
                                value={inches?.toString() || ''} 
                                name={`${name}_in`}
                                onChange={handleHeightInChange} 
                                onBlur={handleOnBlur} 
                                type="Number"
                                validate={validateInches}
                            />
                        </div>   
                    </div>
                    <button 
                        type="button" 
                        className="unit-switch-link" 
                        onClick={handleUnitSwitch}
                    >
                        Switch to metric
                    </button>
                    </div>
                    
                )}
            </div>
        </div>
    )
}

export default HeightInput;
